/**
 * 표 전체를 하나의 viewport에서 가로로 스크롤한다.
 *
 * 열 헤더를 별도 복제하지 않으므로 트랙패드의 탄성 스크롤 중에도 헤더와 본문이
 * 같은 위치를 유지한다. 긴 표에서는 하단 가로 스크롤바가 `position: sticky`로
 * 현재 세로 scrollport 하단에 따라온다 — JS 위치 추적과 달리 컴포지터가 직접
 * 처리하므로 스크롤에 지연 없이 붙는다. 단, 표와 세로 스크롤러 사이의 조상에
 * scroll container(overflow hidden/auto/scroll)가 있으면 sticky가 그쪽에 붙으므로
 * 중간 조상은 `overflow-clip`을 써야 한다.
 */

import { cn } from "@/lib/utils";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ScrollBar } from "./scrollbar";

export interface TableScrollAreaProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  scrollbarSize?: "normal" | "small";
}

export const TableScrollArea = forwardRef<HTMLDivElement, TableScrollAreaProps>(
  (
    { children, className, scrollbarSize = "normal", ...rest },
    forwardedRef,
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

    useImperativeHandle(forwardedRef, () => rootRef.current!, []);

    const updateOverflowState = useCallback(() => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const nextHasOverflow = viewport.scrollWidth > viewport.clientWidth + 1;
      setHasHorizontalOverflow((previous) =>
        previous === nextHasOverflow ? previous : nextHasOverflow,
      );
    }, []);

    useLayoutEffect(updateOverflowState, [updateOverflowState]);

    useEffect(() => {
      const viewport = viewportRef.current;
      if (!viewport) return;

      let animationFrameId: number | null = null;
      const scheduleUpdate = () => {
        if (animationFrameId !== null) return;
        animationFrameId = requestAnimationFrame(() => {
          animationFrameId = null;
          updateOverflowState();
        });
      };

      window.addEventListener("resize", scheduleUpdate);

      const resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(viewport);
      if (viewport.firstElementChild) {
        resizeObserver.observe(viewport.firstElementChild);
      }

      const mutationObserver = new MutationObserver(scheduleUpdate);
      mutationObserver.observe(viewport, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      return () => {
        if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
        window.removeEventListener("resize", scheduleUpdate);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }, [updateOverflowState]);

    return (
      <div ref={rootRef} className={cn("relative", className)} {...rest}>
        <ScrollAreaPrimitive.Root
          type="always"
          data-table-scroll-area-root
          // clip: 시각적 클리핑은 hidden과 같지만 scroll container를 만들지
          // 않아 아래 sticky 래퍼가 바깥 세로 스크롤러를 기준으로 동작한다.
          className="relative overflow-clip"
        >
          <ScrollAreaPrimitive.Viewport
            ref={viewportRef}
            className="h-full w-full rounded-[inherit]"
          >
            {children}
          </ScrollAreaPrimitive.Viewport>
          {hasHorizontalOverflow && (
            /* 래퍼가 flow에서 스크롤바 높이를 차지하고(과거 pb 하드코딩 대체),
               Radix 스크롤바(absolute bottom-0)는 래퍼를 채운다.
               배경은 투명 — 흰 바탕이면 표 아래 흰 띠로 보인다(디자인 확인). */
            <div
              className={cn(
                // bottom 은 --table-scrollbar-bottom 으로 띄울 수 있다 (근거 창처럼 짧은 스크롤 영역에서 끝에서 살짝 띄우기)
                "sticky bottom-[var(--table-scrollbar-bottom,0px)] z-40",
                scrollbarSize === "normal" ? "h-[0.8125rem]" : "h-[0.5625rem]",
              )}
            >
              <ScrollBar
                orientation="horizontal"
                size={scrollbarSize}
                data-table-horizontal-scrollbar
              />
            </div>
          )}
        </ScrollAreaPrimitive.Root>
      </div>
    );
  },
);

TableScrollArea.displayName = "TableScrollArea";
